import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BuildingUtilityBooking } from '../../types';
import { buildingUtilityApi } from '../../api/buildingUtilityApi';
import { incomeExpenseApi, ProjectBankAccount } from '../../api/incomeExpenseApi';
import { formatVndAmount } from '../../utils/vndFormat';
import {
  isUtilityBookingServiceFeeCollection,
  resolveUtilityBookingServiceFee,
} from '../../utils/utilityBookingCost.util';
import {
  getUtilityCollectVoucherModalTitle,
  getUtilityRefundVoucherModalTitle,
} from '../../utils/utilityBookingVoucher.util';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { printVoucherHtml } from '../../utils/voucherPrint';

type DepositAction = 'collect' | 'refund';

type ModalStep = 'form' | 'preview' | 'success';

const toDateInputValue = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

interface UtilityBookingDepositModalProps {
  booking: BuildingUtilityBooking;
  action: DepositAction;
  onClose: () => void;
  onCompleted: () => void;
}

const LedgerVoucherPane: React.FC<{
  entryId: string | null;
  previewHtml: string;
  title: string;
}> = ({ entryId, previewHtml, title }) => {
  const [loading, setLoading] = useState(false);
  const [htmlContent, setHtmlContent] = useState(previewHtml);

  useEffect(() => {
    if (!entryId) {
      setHtmlContent(previewHtml);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const html = await incomeExpenseApi.fetchLedgerVoucherHtml(entryId);
        if (!cancelled) setHtmlContent(html);
      } catch {
        if (!cancelled) {
          toast.error('Không thể tải phiếu');
          setHtmlContent(previewHtml);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [entryId, previewHtml]);

  return (
    <div className="flex-1 overflow-auto bg-gray-100 relative min-h-[50vh]">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
        </div>
      )}
      {htmlContent ? (
        <iframe
          title={title}
          srcDoc={htmlContent}
          className="w-full border-0 bg-white min-h-[50vh]"
          sandbox="allow-same-origin allow-scripts"
        />
      ) : null}
    </div>
  );
};

export const UtilityBookingDepositModal: React.FC<UtilityBookingDepositModalProps> = ({
  booking,
  action,
  onClose,
  onCompleted,
}) => {
  const isCollect = action === 'collect';
  const isServiceFeeCollection = isCollect && isUtilityBookingServiceFeeCollection(booking);
  const [step, setStep] = useState<ModalStep>('form');
  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'BANK_TRANSFER'>('CASH');
  const [paymentDate, setPaymentDate] = useState(toDateInputValue(new Date()));
  const [projectBankAccountId, setProjectBankAccountId] = useState('');
  const [bankAccounts, setBankAccounts] = useState<ProjectBankAccount[]>([]);
  const [amountInput, setAmountInput] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [ledgerEntryId, setLedgerEntryId] = useState<string | null>(null);
  const [journalNumber, setJournalNumber] = useState<string | null>(null);

  const defaultAmount = isServiceFeeCollection
    ? resolveUtilityBookingServiceFee(booking)
    : booking.depositAmount ?? 0;

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const accounts = await incomeExpenseApi.listProjectBankAccounts(booking.buildingId);
        setBankAccounts(accounts || []);
        const defaultAccount = accounts?.find((a) => a.isDefault) || accounts?.[0];
        if (defaultAccount) setProjectBankAccountId(defaultAccount.id);
      } catch {
        toast.error('Không thể tải tài khoản dự án');
      }
    };
    if (booking.buildingId) loadAccounts();
    if (isCollect) {
      setAmountInput(
        defaultAmount > 0 ? new Intl.NumberFormat('vi-VN').format(defaultAmount) : '',
      );
    }
  }, [booking.buildingId, defaultAmount, isCollect]);

  const parseAmount = (value: string) => Number(value.replace(/[^\d]/g, '')) || 0;

  const buildPayload = () => {
    const payload: {
      paymentMethod: 'CASH' | 'BANK_TRANSFER';
      projectBankAccountId?: string;
      paymentDate?: string;
      collectAmount?: number;
    } = {
      paymentMethod,
      paymentDate: paymentDate || undefined,
      projectBankAccountId: projectBankAccountId || undefined,
    };
    if (isCollect) {
      const parsed = parseAmount(amountInput);
      if (parsed > 0) payload.collectAmount = parsed;
    }
    return payload;
  };

  const amountLabel = isCollect ? parseAmount(amountInput) || defaultAmount : defaultAmount;

  const handlePreview = async () => {
    if (amountLabel <= 0) {
      toast.error(
        isCollect
          ? isServiceFeeCollection
            ? 'Nhập số tiền phí hợp lệ'
            : 'Nhập số tiền cọc hợp lệ'
          : 'Chưa có số tiền cọc đã thu để hoàn',
      );
      return;
    }
    setPreviewLoading(true);
    try {
      const payload = buildPayload();
      const result = isCollect
        ? await buildingUtilityApi.previewCollectBookingDeposit(booking.id, payload)
        : await buildingUtilityApi.previewRefundBookingDeposit(booking.id, payload);
      setPreviewHtml(result.html);
      setStep('preview');
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Không thể xem trước phiếu'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirm = async () => {
    setConfirmLoading(true);
    try {
      const payload = buildPayload();
      const result = isCollect
        ? await buildingUtilityApi.collectBookingDeposit(booking.id, payload)
        : await buildingUtilityApi.refundBookingDeposit(booking.id, payload);
      setLedgerEntryId(result.apartmentLedgerEntryId ?? null);
      setJournalNumber(result.journalNumber ?? null);
      setStep('success');
      toast.success(
        isCollect
          ? isServiceFeeCollection
            ? 'Thu phí thành công'
            : 'Thu cọc thành công'
          : 'Hoàn cọc thành công',
      );
      onCompleted();
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Thao tác thất bại'));
    } finally {
      setConfirmLoading(false);
    }
  };

  const title = isCollect
    ? isServiceFeeCollection
      ? 'Thu phí dịch vụ'
      : 'Thu tiền đặt cọc'
    : 'Hoàn tiền đặt cọc';
  const voucherTitle = isCollect
    ? getUtilityCollectVoucherModalTitle(booking)
    : getUtilityRefundVoucherModalTitle();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h3 className="font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">
              {booking.utility?.name} · {booking.apartment?.code}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl leading-none px-2"
            aria-label="Đóng"
          >
            ×
          </button>
        </div>

        {step === 'form' && (
          <div className="p-6 space-y-4 overflow-y-auto">
            {isCollect ? (
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  {isServiceFeeCollection ? 'Số tiền phí' : 'Số tiền cọc'}
                </label>
                <input
                  type="text"
                  value={amountInput}
                  onChange={(e) => setAmountInput(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {defaultAmount > 0
                    ? `Mặc định ${formatVndAmount(defaultAmount)} từ đăng ký — có thể chỉnh khi cần.`
                    : isServiceFeeCollection
                      ? 'Nhập số tiền phí thu tại quầy.'
                      : 'Nhập số tiền cọc thu tại quầy.'}
                </p>
              </div>
            ) : (
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Số tiền hoàn</span>
                  <span className="font-semibold text-gray-900">{formatVndAmount(amountLabel)}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Bằng số tiền cọc đã thu trên đăng ký này.</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Ngày thanh toán</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Hình thức thanh toán</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as 'CASH' | 'BANK_TRANSFER')}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                >
                  <option value="CASH">Tiền mặt</option>
                  <option value="BANK_TRANSFER">Chuyển khoản</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-700 mb-1">Tài khoản dự án</label>
                <select
                  value={projectBankAccountId}
                  onChange={(e) => setProjectBankAccountId(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                >
                  {bankAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                      {account.isDefault ? ' (Mặc định)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handlePreview}
                disabled={previewLoading}
                className="px-4 py-2 text-sm text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:opacity-50"
              >
                {previewLoading ? 'Đang tải...' : 'Xem trước phiếu'}
              </button>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <>
            <LedgerVoucherPane entryId={null} previewHtml={previewHtml} title={`${voucherTitle} (xem trước)`} />
            <div className="border-t px-4 py-3 flex justify-between items-center gap-2">
              <button
                type="button"
                onClick={() => setStep('form')}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Quay lại
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => previewHtml && printVoucherHtml(previewHtml)}
                  className="px-4 py-2 text-sm text-cyan-700 border border-cyan-200 rounded-lg hover:bg-cyan-50"
                >
                  In xem trước
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={confirmLoading}
                  className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {confirmLoading ? 'Đang xử lý...' : 'Xác nhận'}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'success' && (
          <>
            <div className="px-4 py-2 bg-green-50 border-b border-green-100 text-sm text-green-800">
              {journalNumber ? `Đã ghi sổ: ${journalNumber}` : 'Ghi sổ thành công'}
            </div>
            <LedgerVoucherPane
              entryId={ledgerEntryId}
              previewHtml={previewHtml}
              title={voucherTitle}
            />
            <div className="border-t px-4 py-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={async () => {
                  if (ledgerEntryId) {
                    try {
                      const html = await incomeExpenseApi.fetchLedgerVoucherHtml(ledgerEntryId);
                      printVoucherHtml(html);
                    } catch {
                      toast.error('Không thể in phiếu');
                    }
                  }
                }}
                disabled={!ledgerEntryId}
                className="px-4 py-2 text-sm text-cyan-700 border border-cyan-200 rounded-lg hover:bg-cyan-50 disabled:opacity-50"
              >
                In phiếu
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Đóng
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UtilityBookingDepositModal;
